import { DialogComponent } from '@theme/dialog';

const STORAGE_KEY = 'be-age-gate';
const DAY_MS = 86400000;

/**
 * The age gate overlay: a full-screen, pre-rendered dialog available on every
 * page (sections/age-gate.liquid, rendered from layout/theme.liquid). It opens
 * automatically until the visitor confirms they are over 18; the confirmation
 * is remembered in localStorage for the section's configured number of days.
 *
 * Declining swaps the choice for the decline message and leaves the gate up —
 * reloading the page brings the question back, so a mis-tap isn't permanent.
 *
 * DialogComponent's Escape and outside-click handling both funnel through
 * `closeDialog`, so shadowing that field with a gated wrapper is enough to
 * keep the dialog open until the visitor has confirmed. In the theme editor
 * the gate never opens by itself (and is never gated shut) so the merchant
 * can preview it via section select without being locked in.
 */
class AgeGateComponent extends DialogComponent {
  requiredRefs = ['dialog', 'choice', 'declined'];

  #verified = false;

  /**
   * The parent's `closeDialog` is an instance field, not a prototype method,
   * so it is captured here — subclass fields initialize after the parent's —
   * and shadowed below with the gated version.
   */
  #close = this.closeDialog;

  closeDialog = async () => {
    if (!this.#verified && !('designMode' in this.dataset)) return;
    await this.#close();
  };

  connectedCallback() {
    super.connectedCallback();

    // Backstop for close paths that bypass closeDialog (e.g. a native
    // `cancel` fired without the component's own Escape handler running).
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);

    if (!('designMode' in this.dataset) && !this.#isVerified()) {
      this.showDialog();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('cancel', this.#handleCancel);
  }

  /**
   * Remembers the confirmation and closes the gate.
   */
  confirm() {
    const days = Number(this.dataset.rememberDays) || 30;

    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now() + days * DAY_MS));
    } catch {
      // Storage blocked: the visitor is simply asked again on the next page.
    }

    this.#verified = true;
    this.closeDialog();
  }

  /**
   * Swaps the choice for the decline message; the gate stays up.
   */
  decline() {
    const { choice, declined } = this.refs;

    choice.hidden = true;
    declined.hidden = false;
    declined.focus();
  }

  /**
   * @returns {boolean} Whether a previous confirmation is still valid.
   */
  #isVerified() {
    try {
      return Number(localStorage.getItem(STORAGE_KEY)) > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * @param {Event} event - The dialog's `cancel` event.
   */
  #handleCancel = (event) => {
    if (!this.#verified && !('designMode' in this.dataset)) event.preventDefault();
  };
}

if (!customElements.get('be-age-gate-component')) {
  customElements.define('be-age-gate-component', AgeGateComponent);
}
